import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { mentorshipApi, showError, showSuccess } from "../lib";
import { useAuth } from "../context/AuthContext";
import {
  Calendar,
  Clock,
  Video,
  Check,
  Loader,
  User,
  Briefcase,
  Trash2,
  Search,
  ChevronDown,
  ChevronUp,
  ArrowUpDown,
  X,
  Filter,
  ChevronLeft,
  ChevronRight,
  MessageCircle,
} from "lucide-react";

interface Session {
  _id: string;
  mentor: {
    _id: string;
    user?: { name: string; firstName?: string; lastName?: string; avatar?: string };
    specialty?: string;
    company?: string;
    expertise?: string[];
    rating?: number;
    reviewsCount?: number;
    avatar?: string;
  };
  student: { 
    _id: string; 
    name?: string; 
    firstName?: string; 
    lastName?: string; 
    avatar?: string;
    email?: string;
  };
  scheduledAt: string;
  duration: number;
  status: "pending" | "approved" | "confirmed" | "completed" | "cancelled" | "no-show";
  price: number;
  topic: string;
  notes?: string;
  meetingLink?: string;
}

const MentorMentorshipSessionsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [sessions, setSessions] = useState<Session[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [dateFilter, setDateFilter] = useState<{ start: string; end: string }>({ start: "", end: "" });
  const [sortBy, setSortBy] = useState<"" | "date-asc" | "date-desc" | "price-asc" | "price-desc" | "status" | "applied-desc" | "applied-asc">("");
  const [showDateFilter, setShowDateFilter] = useState(false);
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  useEffect(() => {
    if (!user) return;
    fetchSessions();
  }, [user]);

  useEffect(() => {
    setSortBy("date-desc");
  }, []);

  const fetchSessions = async () => {
    if (!user) return;
    try {
      setLoading(true);
      const response = await mentorshipApi.getMySessions({
        role: "mentor",
        status: statusFilter || undefined,
        limit: 100,
      });
      
      // Log the response to see what's happening
      console.log("Sessions response:", JSON.stringify(response.data, null, 2));
      
      setSessions(response.data || []);
      setCurrentPage(1);
    } catch (err) {
      console.error("Failed to load sessions:", err);
      showError("Failed to load sessions");
    } finally {
      setLoading(false);
    }
  };

  const handleCancelSession = async (sessionId: string) => {
    if (!confirm("Decline this session?")) return;
    try {
      const res = await mentorshipApi.cancelSession(sessionId);
      if (res.success) {
        showSuccess("Session declined");
        await fetchSessions();
      }
    } catch (err: any) {
      showError(err.response?.data?.message || "Failed to decline");
    }
  };

  const handleApproveSession = async (sessionId: string) => {
    try {
      const res = await mentorshipApi.confirmSession(sessionId);
      if (res.success) {
        showSuccess(res.data?.meetingLink ? `Approved! Link: ${res.data.meetingLink}` : "Session approved!");
        await fetchSessions();
      }
    } catch (err: any) {
      showError(err.response?.data?.message || "Failed to approve");
    }
  };

  const handleCompleteSession = async (sessionId: string) => {
    if (!confirm("Mark this session as completed?")) return;
    try {
      const res = await mentorshipApi.completeSession(sessionId);
      if (res.success) {
        showSuccess("Session marked as completed");
        await fetchSessions();
      }
    } catch (err: any) {
      showError(err.response?.data?.message || "Failed to mark session as completed");
    }
  };

  const handleDeleteSession = async (sessionId: string, topic: string) => {
    if (!window.confirm(`Are you sure you want to delete this session? This action cannot be undone.\n\nSession: "${topic || 'Mentorship Session'}"`)) {
      return;
    }
    try {
      const res = await mentorshipApi.deleteSession(sessionId);
      if (res.success) {
        showSuccess("Session deleted successfully");
        await fetchSessions();
      }
    } catch (err: any) {
      showError(err.response?.data?.message || "Failed to delete session");
    }
  };

  const handleStartConversation = async (studentId: string) => {
    if (!studentId || studentId === "unknown" || studentId === "undefined") {
      showError("Unable to start conversation: Student ID not found");
      return;
    }
    try {
      navigate(`/dashboard/mentor/messages?userId=${studentId}`);
    } catch (err: any) {
      showError("Failed to start conversation");
    }
  };

  const formatDateTime = (dateStr: string) => {
    const date = new Date(dateStr);
    return {
      date: date.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" }),
      time: date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }),
    };
  };

  const getStatusConfig = (status: string) => {
    const configs: Record<string, { bg: string; text: string; label: string }> = {
      pending: { bg: "bg-amber-100", text: "text-amber-700", label: "Pending" },
      approved: { bg: "bg-yellow-100", text: "text-yellow-700", label: "Waiting for Payment" },
      confirmed: { bg: "bg-blue-100", text: "text-blue-700", label: "Confirmed" },
      completed: { bg: "bg-green-100", text: "text-green-700", label: "Completed" },
      cancelled: { bg: "bg-red-100", text: "text-red-700", label: "Declined" },
      "no-show": { bg: "bg-gray-100", text: "text-gray-700", label: "No Show" },
    };
    return configs[status] || configs.pending;
  };

  const fmtXAF = (xAF: number) =>
    new Intl.NumberFormat("fr-CM", { style: "currency", currency: "XAF", maximumFractionDigits: 0 }).format(xAF);

  // CRITICAL FIX: Enhanced getStudentInfo to handle different data structures
  const getStudentInfo = (session: Session) => {
    // Log the session to see what's happening
    console.log(`Session ${session._id} (${session.status}):`, session.student);
    
    // If student doesn't exist at all
    if (!session.student) {
      console.warn("Session missing student object:", session._id);
      return { name: "Student", avatar: undefined, id: "unknown" };
    }

    const student = session.student;
    
    // Try to get name from various sources
    let name = student.name;
    
    // If name is empty, try firstName + lastName
    if (!name && (student.firstName || student.lastName)) {
      name = [student.firstName, student.lastName].filter(Boolean).join(" ").trim();
    }
    
    // If still no name, check if student object has a 'user' field (nested)
    if (!name && (student as any).user) {
      const user = (student as any).user;
      name = user.name || [user.firstName, user.lastName].filter(Boolean).join(" ").trim();
    }
    
    // If still no name, use "Student" as fallback
    if (!name) {
      name = "Student";
    }

    // Get avatar from various sources
    let avatar = student.avatar;
    if (!avatar && (student as any).user) {
      avatar = (student as any).user.avatar;
    }

    // Get ID from various sources
    let id = student._id;
    if (!id && (student as any).user) {
      id = (student as any).user._id;
    }
    if (!id) {
      id = "unknown";
    }

    return { 
      name, 
      avatar,
      id
    };
  };

  const filteredAndSortedSessions = useMemo(() => {
    let result = [...sessions];

    if (statusFilter) {
      result = result.filter((s) => s.status === statusFilter);
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      result = result.filter((s) => {
        const studentInfo = getStudentInfo(s);
        const studentName = studentInfo.name.toLowerCase();
        const topic = (s.topic || "").toLowerCase();
        return studentName.includes(query) || topic.includes(query);
      });
    }

    if (dateFilter.start || dateFilter.end) {
      result = result.filter((s) => {
        const sessionDate = new Date(s.scheduledAt).getTime();
        if (dateFilter.start && sessionDate < new Date(dateFilter.start).setHours(0, 0, 0, 0)) {
          return false;
        }
        if (dateFilter.end && sessionDate > new Date(dateFilter.end).setHours(23, 59, 59, 999)) {
          return false;
        }
        return true;
      });
    }

    if (sortBy) {
      result.sort((a, b) => {
        switch (sortBy) {
          case "date-asc":
            return new Date(a.scheduledAt).getTime() - new Date(b.scheduledAt).getTime();
          case "date-desc":
            return new Date(b.scheduledAt).getTime() - new Date(a.scheduledAt).getTime();
          case "price-asc":
            return a.price - b.price;
          case "price-desc":
            return b.price - a.price;
          case "applied-asc":
            return new Date(a.createdAt || a.scheduledAt).getTime() - new Date(b.createdAt || b.scheduledAt).getTime();
          case "applied-desc":
            return new Date(b.createdAt || b.scheduledAt).getTime() - new Date(a.createdAt || a.scheduledAt).getTime();
          case "status":
            const statusOrder: Record<string, number> = { 
              pending: 0, 
              approved: 1, 
              confirmed: 2, 
              completed: 3, 
              cancelled: 4, 
              "no-show": 5 
            };
            return statusOrder[a.status] - statusOrder[b.status];
          default:
            return 0;
        }
      });
    }

    return result;
  }, [sessions, statusFilter, searchQuery, dateFilter, sortBy]);

  // Pagination calculations
  const totalItems = filteredAndSortedSessions.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, totalItems);
  const currentItems = filteredAndSortedSessions.slice(startIndex, endIndex);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleItemsPerPageChange = (value: number) => {
    setItemsPerPage(value);
    setCurrentPage(1);
  };

  const statusCounts = useMemo(() => ({
    all: sessions.length,
    pending: sessions.filter((s) => s.status === "pending").length,
    approved: sessions.filter((s) => s.status === "approved").length,
    confirmed: sessions.filter((s) => s.status === "confirmed").length,
    completed: sessions.filter((s) => s.status === "completed").length,
    cancelled: sessions.filter((s) => s.status === "cancelled").length,
  }), [sessions]);

  const activeFilterCount = (statusFilter ? 1 : 0) + (searchQuery.trim() ? 1 : 0) + (dateFilter.start || dateFilter.end ? 1 : 0) + (sortBy ? 1 : 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen pt-16 bg-gray-50">
        <Loader className="w-6 h-6 text-primary animate-spin" />
      </div>
    );
  }

  return (
    <main className="min-h-screen pt-12 pb-12 bg-gray-50">
      <div className="max-w-2xl px-3 mx-auto">
        {/* Header */}
        <div className="py-4">
          <h1 className="text-xl font-bold text-gray-900">My Sessions</h1>
          <p className="text-xs text-gray-500">Manage student sessions</p>
        </div>

        {/* Search and Controls */}
        <div className="mb-4 space-y-3">
          {/* Search & Quick Filters */}
          <div className="flex flex-wrap items-stretch gap-2">
            <div className="relative flex-1 min-w-[140px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                }}
                placeholder="Search by student or topic..."
                className="w-full py-2 pr-4 text-xs transition-colors bg-transparent border border-gray-200 rounded-lg pl-9 focus:border-gray-400 focus:ring-0"
              />
              {searchQuery && (
                <button
                  onClick={() => {
                    setSearchQuery("");
                    setCurrentPage(1);
                  }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 hover:bg-gray-100 rounded"
                >
                  <X className="w-3 h-3 text-gray-400" />
                </button>
              )}
            </div>

            {/* Date Filter Button */}
            <button
              onClick={() => {
                setShowDateFilter(!showDateFilter);
                setShowSortDropdown(false);
              }}
              className={`px-3 py-2 rounded-lg text-xs font-medium transition-all flex items-center gap-1.5 ${
                dateFilter.start || dateFilter.end
                  ? "bg-blue-50 text-blue-700 border border-blue-200"
                  : "bg-white text-gray-600 border border-gray-200 hover:border-gray-300"
              }`}
            >
              <Filter className="w-3.5 h-3.5" />
              <span>Date</span>
              {(dateFilter.start || dateFilter.end) && (
                <span className="ml-0.5 w-4 h-4 rounded-full bg-blue-600 text-white text-[10px] flex items-center justify-center">•</span>
              )}
            </button>

            {/* Sort Dropdown */}
            <div className="relative">
              <button
                onClick={() => {
                  setShowSortDropdown(!showSortDropdown);
                  setShowDateFilter(false);
                }}
                className={`px-3 py-2 rounded-lg text-xs font-medium transition-all flex items-center gap-1.5 ${
                  sortBy
                    ? "bg-gray-900 text-white border border-gray-900"
                    : "bg-white text-gray-600 border border-gray-200 hover:border-gray-300"
                }`}
              >
                <ArrowUpDown className="w-3.5 h-3.5" />
                <span>{sortBy ? (sortBy === "date-asc" ? "Date ↑" : sortBy === "date-desc" ? "Date ↓" : sortBy === "price-asc" ? "Price ↑" : sortBy === "price-desc" ? "Price ↓" : "Status") : "Sort"}</span>
                {showSortDropdown ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
              </button>
              {showSortDropdown && (
                <div className="absolute top-full left-0 mt-1 py-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 min-w-[140px]">
                   {["", "date-desc", "date-asc", "price-asc", "price-desc", "status", "applied-desc", "applied-asc"].map((opt) => (
                    <button
                      key={opt}
                      onClick={() => { 
                        setSortBy(opt as any); 
                        setShowSortDropdown(false);
                        setCurrentPage(1);
                      }}
                      className={`w-full px-3 py-1.5 text-left text-xs hover:bg-gray-50 ${
                        sortBy === opt ? "bg-gray-50 text-gray-900 font-medium" : "text-gray-600"
                      }`}
                    >
                       {opt === "" ? "Clear sort" : opt === "date-desc" ? "Date: New → Old" : opt === "date-asc" ? "Date: Old → New" : opt === "price-asc" ? "Price: Low → High" : opt === "price-desc" ? "Price: High → Low" : opt === "status" ? "By Status" : opt === "applied-desc" ? "Applied: New → Old" : "Applied: Old → New"}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Active Filters Summary */}
            {activeFilterCount > 0 && (
              <button
                onClick={() => {
                  setStatusFilter("");
                  setSearchQuery("");
                  setDateFilter({ start: "", end: "" });
                  setSortBy("");
                  setShowDateFilter(false);
                  setShowSortDropdown(false);
                  setCurrentPage(1);
                }}
                className="flex items-center gap-1 px-3 py-2 text-xs font-medium text-gray-700 transition-colors bg-gray-100 border border-gray-200 rounded-lg hover:bg-gray-200"
              >
                Clear all ({activeFilterCount})
              </button>
            )}
          </div>

          {/* Date Range Picker */}
          {showDateFilter && (
            <div className="flex flex-wrap items-end gap-3 p-3 bg-white border border-gray-200 rounded-lg shadow-sm">
              <div className="flex-1 min-w-[160px]">
                <label className="block text-[10px] font-medium text-gray-500 uppercase mb-1">Start Date</label>
                <input
                  type="date"
                  value={dateFilter.start}
                  onChange={(e) => {
                    setDateFilter({ ...dateFilter, start: e.target.value });
                    setCurrentPage(1);
                  }}
                  className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:border-gray-400 focus:ring-0"
                />
              </div>
              <div className="flex-1 min-w-[160px]">
                <label className="block text-[10px] font-medium text-gray-500 uppercase mb-1">End Date</label>
                <input
                  type="date"
                  value={dateFilter.end}
                  onChange={(e) => {
                    setDateFilter({ ...dateFilter, end: e.target.value });
                    setCurrentPage(1);
                  }}
                  className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:border-gray-400 focus:ring-0"
                />
              </div>
              {(dateFilter.start || dateFilter.end) && (
                <button
                  onClick={() => {
                    setDateFilter({ start: "", end: "" });
                    setCurrentPage(1);
                  }}
                  className="px-3 py-1.5 text-xs text-red-600 hover:bg-red-50 rounded-lg border border-red-100"
                >
                  Clear dates
                </button>
              )}
            </div>
          )}

          {/* Status Pills */}
          <div className="flex flex-wrap gap-2">
            {[
              { key: "", label: "All", count: statusCounts.all },
              { key: "pending", label: "Pending", count: statusCounts.pending },
              { key: "approved", label: "Waiting Payment", count: statusCounts.approved },
              { key: "confirmed", label: "Confirmed", count: statusCounts.confirmed },
              { key: "completed", label: "Completed", count: statusCounts.completed },
              { key: "cancelled", label: "Cancelled", count: statusCounts.cancelled },
            ].map((item) => (
              <button
                key={item.key || "all"}
                onClick={() => {
                  setStatusFilter(item.key);
                  setCurrentPage(1);
                }}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                  statusFilter === item.key
                    ? "bg-gray-900 text-white shadow-sm"
                    : "bg-white text-gray-600 border border-gray-200 hover:border-gray-300"
                }`}
              >
                {item.label}
                <span className={`ml-1.5 px-1.5 py-0.5 rounded-full text-[10px] ${
                  statusFilter === item.key ? "bg-white/20" : "bg-gray-100"
                }`}>
                  {item.count}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Results count and items per page */}
        {filteredAndSortedSessions.length > 0 && (
          <div className="flex items-center justify-between mb-3 text-xs text-gray-500">
            <span>
              Showing {startIndex + 1} - {endIndex} of {totalItems} sessions
            </span>
            <div className="flex items-center gap-2">
              <span>Show:</span>
              <select
                value={itemsPerPage}
                onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                className="px-2 py-1 text-xs bg-white border border-gray-200 rounded focus:border-gray-400 focus:ring-0"
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </select>
            </div>
          </div>
        )}

        {/* Sessions List */}
        {currentItems.length === 0 ? (
          <div className="py-12 text-center bg-white border border-gray-200 rounded-xl">
            <Calendar className="w-8 h-8 mx-auto mb-3 text-gray-300" />
            <p className="mb-4 text-sm text-gray-500">
              {sessions.length === 0 ? "No sessions yet" : "No sessions match your filters"}
            </p>
            <button onClick={() => navigate("/mentor/dashboard")} className="px-4 py-2 text-xs font-medium text-white rounded-lg bg-primary">
              Dashboard
            </button>
          </div>
        ) : (
          <>
            <div className="space-y-3">
              {currentItems.map((session) => {
                const studentInfo = getStudentInfo(session);
                const { date, time } = formatDateTime(session.scheduledAt);
                const statusConfig = getStatusConfig(session.status);

                return (
                  <div key={session._id} className="p-4 transition-shadow bg-white border border-gray-200 shadow-sm rounded-xl hover:shadow-md">
                    {/* Student Info */}
                    <div className="flex items-center gap-3 mb-3">
                      <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 shrink-0">
                        {studentInfo.avatar ? (
                          <img src={studentInfo.avatar} className="object-cover w-10 h-10 rounded-full" alt={studentInfo.name} />
                        ) : (
                          <User className="w-5 h-5 text-primary" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h6 className="text-sm font-semibold text-gray-900 truncate">
                          {studentInfo.name}
                        </h6>
                        <p className="text-xs text-gray-500 truncate">{session.topic || "Mentorship Session"}</p>
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusConfig.bg} ${statusConfig.text}`}>
                        {statusConfig.label}
                      </span>
                    </div>

                    {/* Details */}
                    <div className="flex flex-wrap items-center gap-4 mb-3 text-xs text-gray-500">
                      <div className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        <span>{date}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        <span>{time}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Briefcase className="w-3 h-3" />
                        <span>{fmtXAF(session.price)}</span>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex flex-wrap gap-2">
                      {session.status === "pending" && (
                        <>
                          <button 
                            onClick={() => handleApproveSession(session._id)} 
                            className="px-3 py-1.5 bg-emerald-600 text-white text-xs font-medium rounded-lg flex items-center gap-1.5 hover:bg-emerald-700 transition-colors"
                          >
                            <Check className="w-3 h-3" /> Approve
                          </button>
                          <button 
                            onClick={() => handleCancelSession(session._id)} 
                            className="px-3 py-1.5 bg-white border border-red-200 text-red-600 text-xs font-medium rounded-lg hover:bg-red-50 transition-colors"
                          >
                            Decline
                          </button>
                        </>
                      )}

                      {session.status === "approved" && (
                        <span className="px-3 py-1.5 bg-yellow-100 text-yellow-700 text-xs font-medium rounded-lg flex items-center gap-1.5">
                          <Clock className="w-3 h-3" /> Waiting for payment
                        </span>
                      )}

                      {session.meetingLink && session.status === "confirmed" && (
                        <>
                          <button 
                            onClick={() => navigate(`/video-room/${session._id}`)} 
                            className="px-3 py-1.5 bg-primary text-white text-xs font-medium rounded-lg flex items-center gap-1.5 hover:bg-primary/90 transition-colors"
                          >
                            <Video className="w-3 h-3" /> Join Session
                          </button>
                          <a 
                            href={session.meetingLink} 
                            target="_blank" 
                            rel="noopener noreferrer" 
                            className="px-3 py-1.5 bg-gray-100 text-gray-700 text-xs font-medium rounded-lg flex items-center gap-1.5 hover:bg-gray-200 transition-colors"
                          >
                            <Video className="w-3 h-3" /> Open Zoom
                          </a>
                        </>
                      )}

                      {session.status === "confirmed" && (
                        <button 
                          onClick={() => handleCompleteSession(session._id)} 
                          className="px-3 py-1.5 bg-emerald-600 text-white text-xs font-medium rounded-lg flex items-center gap-1.5 hover:bg-emerald-700 transition-colors"
                        >
                          <Check className="w-3 h-3" /> Complete
                        </button>
                      )}

                      {/* Message Student - Available for all statuses except cancelled and pending */}
                      {session.status !== "cancelled" && session.status !== "pending" && studentInfo.id !== "unknown" && (
                        <button
                          onClick={() => handleStartConversation(studentInfo.id)}
                          className="px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded-lg flex items-center gap-1.5 hover:bg-blue-700 transition-colors"
                        >
                          <MessageCircle className="w-3 h-3" /> Message
                        </button>
                      )}

                      {/* Delete button - available for all statuses except "approved" (waiting for payment) */}
                      {session.status !== "approved" && (
                        <button 
                          onClick={() => handleDeleteSession(session._id, session.topic)}
                          className="px-3 py-1.5 bg-white border border-red-200 text-red-600 text-xs font-medium rounded-lg flex items-center gap-1.5 hover:bg-red-50 transition-colors"
                          title="Delete session permanently"
                        >
                          <Trash2 className="w-3 h-3" /> Delete
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex flex-col items-center justify-between gap-3 mt-6 sm:flex-row">
                <div className="text-xs text-gray-500">
                  Page {currentPage} of {totalPages}
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1 ${
                      currentPage === 1
                        ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                        : "bg-white border border-gray-200 text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    <ChevronLeft className="w-3.5 h-3.5" />
                    Previous
                  </button>
                  
                  <div className="flex items-center gap-1">
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }
                      
                      return (
                        <button
                          key={pageNum}
                          onClick={() => handlePageChange(pageNum)}
                          className={`w-8 h-8 rounded-lg text-xs font-medium transition-colors ${
                            currentPage === pageNum
                              ? "bg-gray-900 text-white"
                              : "bg-white border border-gray-200 text-gray-700 hover:bg-gray-50"
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>

                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1 ${
                      currentPage === totalPages
                        ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                        : "bg-white border border-gray-200 text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    Next
                    <ChevronRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </main>
  );
};

export default MentorMentorshipSessionsPage;